import { TasksSource } from "@jugl-web/rest-api/tasks";
import { useAppVariant } from "@jugl-web/utils";
import {
  useLocalStorage,
  useSessionStorage,
} from "@jugl-web/utils/hooks/useStorage";
import { useCallback, useMemo } from "react";
import { TaskListView } from "../types";

// #region Shared Types and Utilities
interface ProduceTasksPageStorageHookOptionsBase {
  storageName: string;
  storageType: "local" | "session";
}

type ProducedHookResult<TValue> = [
  value: TValue,
  setValue: (newValue: TValue | ((previousValue: TValue) => TValue)) => void
];

type EntityScreenId = string;
type TaskViewId = string;
// #endregion

// #region Key Builders
/**
 * Builds a composite ID for a given entity and screen ID (based on tasks source).
 * Screen ID can refer to My Tasks, Team Tasks, Customer Tasks, or a specific board (via board ID).
 *
 * @example
 * 6a0e82a1-my -> refers to My Tasks in `6a0e82a1` entity
 * 6a0e82a1-customer -> refers to Customer Tasks in `6a0e82a1` entity
 * 6a0e82a1-bb866a47 -> refers to Board `bb866a47` in `6a0e82a1` entity
 */
const buildEntityScreenId = (
  entityId: string,
  source: TasksSource
): EntityScreenId =>
  `${entityId}-${source.type === "boardTasks" ? source.boardId : "customer"}`;

/**
 * Builds a task view ID for a given task view.
 *
 * @example
 * { type: "kanban" } -> "kanban"
 * { type: "table" } -> "table"
 * { type: "forms"; formId: "123"; formPrefix: "3CA1" } -> "forms-3CA1-123"
 */
const buildTaskViewId = (taskView: TaskListView): TaskViewId =>
  taskView.type === "forms"
    ? `forms-${taskView.formPrefix}-${taskView.formId}`
    : taskView.type;

/**
 * Builds a storage key for a given storage name.
 *
 * @example
 * "FILTERING" -> "JUGL__TASKS_PAGE_FILTERING"
 * "SORTING" -> "JUGL__TASKS_PAGE_SORTING"
 */
const buildStorageKey = (storageName: string): string =>
  `JUGL__TASKS_PAGE_${storageName}`;
// #endregion

// #region Shared Storage (grouped only by entityScreenId)
export interface ProduceTasksPageStorageHookOptions<TValue>
  extends ProduceTasksPageStorageHookOptionsBase {
  getInitialValue: (context: {
    isDemo: boolean;
    tasksSource: TasksSource;
  }) => TValue;
}

export interface ProducedSharedHookOptions {
  entityId: string;
  tasksSource: TasksSource;
}

type SharedTasksPageStorage<TValue> = {
  [key: EntityScreenId]: TValue;
};

/**
 * Creates a storage hook factory for shared state (not view-specific).
 *
 * Storage structure:
 * ```
 * "JUGL_TASKS_PAGE_<STORAGE_NAME>": {
 *   "<ENTITY_ID>-<SCREEN_ID>": <VALUE>
 * }
 * ```
 *
 * @example
 * // hooks/useTaskListViewStorage.ts
 * const useTaskListViewStorage = produceTasksPageStorageHook({
 *   storageName: "LIST_VIEW",
 *   storageType: "local",
 *   initialValue: { type: "kanban" }
 * });
 *
 * // modules/tasks/pages/TasksPage.tsx
 * const [taskListView, setTaskListView] = useTaskListViewStorage({
 *   entityId,
 *   tasksSource,
 * });
 */
export const produceTasksPageStorageHook = <TValue>({
  storageName,
  storageType,
  getInitialValue,
}: ProduceTasksPageStorageHookOptions<TValue>): ((
  options: ProducedSharedHookOptions
) => ProducedHookResult<TValue>) => {
  const storageKey = buildStorageKey(storageName);

  const useStorage =
    storageType === "local" ? useLocalStorage : useSessionStorage;

  return ({ entityId, tasksSource }) => {
    const entityScreenId = useMemo(
      () => buildEntityScreenId(entityId, tasksSource),
      [entityId, tasksSource]
    );

    const [storage, setStorage] = useStorage<SharedTasksPageStorage<TValue>>(
      storageKey,
      {}
    );

    const { isDemo } = useAppVariant();

    const initialValue = useMemo(
      () => getInitialValue({ isDemo, tasksSource }),
      [isDemo, tasksSource]
    );

    const value = useMemo(
      () => storage[entityScreenId] ?? initialValue,
      [storage, entityScreenId, initialValue]
    );

    const setValue = useCallback(
      (valueOrCallback: TValue | ((previousValue: TValue) => TValue)) => {
        const resolvedValue =
          valueOrCallback instanceof Function
            ? valueOrCallback(value)
            : valueOrCallback;

        setStorage((previousStorage) => ({
          ...previousStorage,
          [entityScreenId]: resolvedValue,
        }));
      },
      [value, setStorage, entityScreenId]
    );

    return [value, setValue];
  };
};
// #endregion

// #region View-Specific Storage (grouped by entityScreenId and taskViewId)
export interface ProduceTasksPageViewSpecificStorageHookOptions<TValue>
  extends ProduceTasksPageStorageHookOptionsBase {
  getInitialValue: (context: {
    isDemo: boolean;
    tasksSource: TasksSource;
    taskView: TaskListView;
  }) => TValue;
}

export interface ProducedViewSpecificHookOptions {
  entityId: string;
  tasksSource: TasksSource;
  taskView: TaskListView;
}

type ViewSpecificTasksPageStorage<TValue> = {
  [key: EntityScreenId]: {
    [key: TaskViewId]: TValue;
  };
};

/**
 * Creates a storage hook factory for view-specific state.
 *
 * Storage structure:
 * ```
 * "JUGL_TASKS_PAGE_<STORAGE_NAME>": {
 *   "<ENTITY_ID>-<SCREEN_ID>": {
 *     "<TASK_VIEW_ID>": <VALUE>
 *   }
 * }
 * ```
 *
 * @example
 * // hooks/useTaskListFilteringStorage.ts
 * const useTaskListFilteringStorage = produceTasksPageViewSpecificStorageHook({
 *   storageName: "FILTERING",
 *   storageType: "local",
 *   initialValue: {
 *    type: "standard",
 *    filters: { assignees: [null, "123"], statuses: ["1"] },
 *   }
 * });
 *
 * // modules/tasks/pages/TasksPage.tsx
 * const [filtering, setFiltering] = useTaskListFilteringStorage({
 *   entityId,
 *   tasksSource,
 *   taskView,
 * });
 */
export const produceTasksPageViewSpecificStorageHook = <TValue>({
  storageName,
  storageType,
  getInitialValue,
}: ProduceTasksPageViewSpecificStorageHookOptions<TValue>): ((
  options: ProducedViewSpecificHookOptions
) => ProducedHookResult<TValue>) => {
  const storageKey = buildStorageKey(storageName);

  const useStorage =
    storageType === "local" ? useLocalStorage : useSessionStorage;

  return ({ entityId, tasksSource, taskView }) => {
    const entityScreenId = useMemo(
      () => buildEntityScreenId(entityId, tasksSource),
      [entityId, tasksSource]
    );

    const taskViewId = useMemo(() => buildTaskViewId(taskView), [taskView]);

    const [storage, setStorage] = useStorage<
      ViewSpecificTasksPageStorage<TValue>
    >(storageKey, {});

    const { isDemo } = useAppVariant();

    const initialValue = useMemo(
      () => getInitialValue({ isDemo, tasksSource, taskView }),
      [isDemo, tasksSource, taskView]
    );

    const value = useMemo(
      () => storage[entityScreenId]?.[taskViewId] ?? initialValue,
      [storage, entityScreenId, taskViewId, initialValue]
    );

    const setValue = useCallback(
      (valueOrCallback: TValue | ((previousValue: TValue) => TValue)) => {
        const resolvedValue =
          valueOrCallback instanceof Function
            ? valueOrCallback(value)
            : valueOrCallback;

        setStorage((previousStorage) => ({
          ...previousStorage,
          [entityScreenId]: {
            ...previousStorage[entityScreenId],
            [taskViewId]: resolvedValue,
          },
        }));
      },
      [value, setStorage, entityScreenId, taskViewId]
    );

    return [value, setValue];
  };
};
// #endregion
